/**
 * Production enrollment/renew/disable orchestration for Lot 4B.
 * Ownership is checked before every side effect using live session epoch.
 * Pending enrollment identity recovers lost register responses without taking
 * over a newer unrelated enrollment.
 */

import {
  clearExplicitPushOptIn,
  clearPendingPushEnrollment,
  isProspectPushOperationLive,
  readPendingPushEnrollment,
  writeExplicitPushOptIn,
  writePendingPushEnrollment,
} from './prospectWebPushClient.js';
import {
  getProspectPushSessionEpoch,
} from './prospectWebPushSession.js';

function createClientOperationId(
  randomUUID = globalThis.crypto?.randomUUID?.bind(globalThis.crypto),
) {
  if (typeof randomUUID === 'function') {
    const id = randomUUID();
    if (typeof id === 'string' && id) return id;
  }
  return `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, '0').slice(-12)}`;
}

/**
 * Recover local consent from server status when pending enrollment matches.
 */
export function recoverExplicitOptInFromStatus({
  uid,
  pending,
  status,
  storage,
} = {}) {
  if (!pending || !status || status.status !== 'active') return null;
  if (pending.installationId !== status.installationId) return null;
  if (
    pending.clientOperationId &&
    status.lastClientOperationId &&
    pending.clientOperationId !== status.lastClientOperationId
  ) {
    // Newer unrelated enrollment — do not take over.
    clearPendingPushEnrollment(storage);
    return { ok: false, reason: 'newer_enrollment' };
  }
  if (
    pending.clientOperationId &&
    status.lastClientOperationId &&
    pending.clientOperationId === status.lastClientOperationId
  ) {
    writeExplicitPushOptIn(
      {
        uid,
        installationId: status.installationId,
        registrationGeneration: status.registrationGeneration,
        enrolledAt: status.enrolledAt,
      },
      storage,
    );
    clearPendingPushEnrollment(storage);
    return {
      ok: true,
      registered: {
        ok: true,
        status: 'active',
        installationId: status.installationId,
        registrationGeneration: status.registrationGeneration,
        enrolledAt: status.enrolledAt,
        lastClientOperationId: status.lastClientOperationId,
      },
    };
  }
  // Pending without matching server op id — still uncertain.
  return { ok: false, reason: 'uncertain', status };
}

export async function commitProspectPushRegistration({
  intent,
  uid,
  installationId,
  token,
  locale,
  expectedRegistrationGeneration = null,
  clientOperationId = null,
  requestGeneration,
  requestSessionEpoch,
  ownership,
  register,
  unregister,
  getStatus = null,
  registerDeps,
  storage,
} = {}) {
  const live = () =>
    isProspectPushOperationLive({
      ...ownership,
      requestGeneration,
      requestSessionEpoch,
      requestAuthUid: uid,
      requestInstallationId: installationId,
      getSessionEpoch: getProspectPushSessionEpoch,
    });

  if (!live()) {
    return { ok: false, reason: 'stale_before_register' };
  }

  const operationId =
    clientOperationId ||
    (intent === 'enroll' ? createClientOperationId() : null);

  if (intent === 'enroll' && operationId) {
    writePendingPushEnrollment(
      {
        uid,
        installationId,
        clientOperationId: operationId,
        intent: 'enroll',
        startedAt: new Date().toISOString(),
      },
      storage,
    );
  }

  const payload = { installationId, token, locale, intent };
  if (intent === 'renew' && expectedRegistrationGeneration != null) {
    payload.expectedRegistrationGeneration = expectedRegistrationGeneration;
  }
  if (operationId) payload.clientOperationId = operationId;

  let registered;
  try {
    registered = await register(payload, registerDeps);
  } catch (error) {
    // Unknown outcome: server may have committed. Keep pending for recovery.
    if (intent === 'enroll' && operationId) {
      // Attempt status recovery immediately when possible.
      if (typeof getStatus === 'function' && live()) {
        try {
          const status = await getStatus({ installationId }, registerDeps);
          const recovered = recoverExplicitOptInFromStatus({
            uid,
            pending: readPendingPushEnrollment(uid, storage),
            status,
            storage,
          });
          if (recovered?.ok) return recovered;
          if (recovered?.reason === 'newer_enrollment') {
            clearPendingPushEnrollment(storage);
            return { ok: false, reason: 'newer_enrollment', status };
          }
        } catch {
          /* keep pending */
        }
      }
      return {
        ok: false,
        reason: 'unknown_outcome',
        pending: true,
        clientOperationId: operationId,
        error,
      };
    }
    throw error;
  }

  if (!live()) {
    if (intent === 'enroll' && registered?.status === 'active' && !registered?.stale) {
      try {
        await unregister(
          {
            installationId,
            expectedRegistrationGeneration: registered.registrationGeneration,
          },
          registerDeps,
        );
        clearPendingPushEnrollment(storage);
      } catch {
        // Compensation failed — retain recoverable pending + generation.
        writePendingPushEnrollment(
          {
            uid,
            installationId,
            clientOperationId: operationId,
            intent: 'enroll',
            startedAt: new Date().toISOString(),
            needsCompensation: true,
            registrationGeneration: registered.registrationGeneration,
            enrolledAt: registered.enrolledAt,
          },
          storage,
        );
      }
    }
    return { ok: false, reason: 'stale_after_register', registered };
  }

  if (registered?.stale === true && intent === 'renew') {
    return { ok: false, reason: 'renew_stale', registered };
  }
  if (registered?.stale === true && intent === 'enroll') {
    // Conflict with a different active enrollment.
    if (operationId) {
      const pending = readPendingPushEnrollment(uid, storage);
      if (
        pending?.clientOperationId === operationId &&
        registered.lastClientOperationId === operationId
      ) {
        // Idempotent recovery path (should be non-stale, but be safe).
      } else {
        clearPendingPushEnrollment(storage);
        return { ok: false, reason: 'enroll_conflict', registered };
      }
    } else {
      return { ok: false, reason: 'enroll_conflict', registered };
    }
  }
  if (registered?.status !== 'active') {
    clearPendingPushEnrollment(storage);
    return { ok: false, reason: 'not_active', registered };
  }

  writeExplicitPushOptIn(
    {
      uid,
      installationId,
      registrationGeneration: registered.registrationGeneration,
      enrolledAt: registered.enrolledAt,
    },
    storage,
  );
  clearPendingPushEnrollment(storage);

  if (!live()) {
    clearExplicitPushOptIn(storage);
    if (intent === 'enroll') {
      try {
        await unregister(
          {
            installationId,
            expectedRegistrationGeneration: registered.registrationGeneration,
          },
          registerDeps,
        );
        clearPendingPushEnrollment(storage);
      } catch {
        writePendingPushEnrollment(
          {
            uid,
            installationId,
            clientOperationId: operationId,
            intent: 'enroll',
            startedAt: new Date().toISOString(),
            needsCompensation: true,
            registrationGeneration: registered.registrationGeneration,
            enrolledAt: registered.enrolledAt,
          },
          storage,
        );
      }
    }
    return { ok: false, reason: 'stale_after_local_write', registered };
  }

  return { ok: true, registered };
}

/**
 * Disable with generation CAS. On stale-active, return conflict so UI can reconcile.
 */
export async function commitProspectPushDisable({
  uid,
  installationId,
  expectedRegistrationGeneration,
  requestGeneration,
  requestSessionEpoch,
  ownership,
  unregister,
  unregisterDeps,
  storage,
} = {}) {
  const live = () =>
    isProspectPushOperationLive({
      ...ownership,
      requestGeneration,
      requestSessionEpoch,
      requestAuthUid: uid,
      requestInstallationId: installationId,
      getSessionEpoch: getProspectPushSessionEpoch,
    });

  if (!live()) return { ok: false, reason: 'stale_before_unregister' };

  const payload = { installationId };
  if (expectedRegistrationGeneration != null) {
    payload.expectedRegistrationGeneration = expectedRegistrationGeneration;
  }
  const outcome = await unregister(payload, unregisterDeps);
  if (!live()) return { ok: false, reason: 'stale_after_unregister', outcome };

  if (outcome?.stale === true && outcome.status === 'active') {
    return {
      ok: false,
      reason: 'disable_conflict',
      outcome,
      currentRegistrationGeneration: outcome.registrationGeneration ?? null,
    };
  }

  clearExplicitPushOptIn(storage);
  clearPendingPushEnrollment(storage);
  return { ok: true, outcome };
}
